<template>
  <el-dialog
    :title="mode === 'create' ? '新增模板' : '编辑模板'"
    :visible.sync="dialogVisible"
    width="760px"
    @close="handleClose"
  >
    <el-form
      ref="formRef"
      :model="formData"
      :rules="formRules"
      label-width="100px"
    >
      <el-form-item label="模板名称" prop="templateName">
        <el-input
          v-model.trim="formData.templateName"
          size="small"
          placeholder="请输入"
          style="width: 320px"
        />
      </el-form-item>

      <el-form-item label="联营模式">
        <div class="mode-title">收益共享</div>
        <div class="mode-content">
          <div
            v-for="(item, index) in formData.ruleList"
            :key="index"
            class="edit-rule-row"
          >
            <el-input
              :value="item.capitalRatio"
              size="small"
              placeholder="请输入出资比例"
              @input="onPercentInput(item, 'capitalRatio', $event)"
            >
              <template slot="append">%</template>
            </el-input>
            <el-input
              :value="item.incomeRate"
              size="small"
              placeholder="请输入收益率"
              @input="onPercentInput(item, 'incomeRate', $event)"
            >
              <template slot="append">%</template>
            </el-input>
            <i
              v-if="formData.ruleList.length > 1"
              class="el-icon-remove-outline remove-icon"
              @click="removeRule(index)"
            />
          </div>

          <div class="add-row" @click="addRule">
            <i class="el-icon-circle-plus-outline" />
            <span>添加</span>
          </div>
        </div>
      </el-form-item>
    </el-form>
    <span slot="footer" class="dialog-footer">
      <el-button size="small" @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" size="small" @click="handleSubmit">保存</el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  name: "StoreCooperationConfigEditDialog",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "create",
    },
    initialData: {
      type: Object,
      default: () => ({
        templateName: "",
        ruleList: [],
      }),
    },
  },
  data() {
    return {
      formData: {
        templateName: "",
        ruleList: [],
      },
      formRules: {
        templateName: [
          { required: true, message: "请输入模板名称", trigger: "blur" },
        ],
      },
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit("update:visible", val);
      },
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.syncFormData();
      }
    },
  },
  methods: {
    /** 打开弹窗时从 initialData 同步到 formData，并清除上次校验状态 */
    syncFormData() {
      this.formData = {
        templateName: this.initialData.templateName || "",
        ruleList: (this.initialData.ruleList || []).map((item) => ({ ...item })),
      };
      this.$nextTick(() => {
        this.$refs.formRef?.clearValidate();
      });
    },
    /** 收益共享区域新增一行（空出资比例、收益率） */
    addRule() {
      this.formData.ruleList.push({ capitalRatio: null, incomeRate: null });
    },
    /** 删除指定下标的收益共享行 */
    removeRule(index) {
      this.formData.ruleList.splice(index, 1);
    },
    /** 出资比例/收益率输入：仅数字、最多三位，数值上限 100，写回 row[key] */
    onPercentInput(row, key, value) {
      const digitText = String(value || "").replace(/[^\d]/g, "").slice(0, 3);
      if (!digitText) {
        row[key] = "";
        return;
      }
      const num = Number(digitText);
      row[key] = String(Math.min(num, 100));
    },
    /** 是否为 1～100 的合法整数百分比（用于提交前校验） */
    isValidPercent(value) {
      return /^(100|[1-9]\d?)$/.test(String(value || ""));
    },
    /** 校验表单与收益共享规则，通过后向父组件 submit 提交数据 */
    handleSubmit() {
      this.$refs.formRef.validate((valid) => {
        if (!valid) {
          return;
        }
        const hasCompleteRow = this.formData.ruleList.some((item) => {
          return (
            this.isValidPercent(item.capitalRatio) &&
            this.isValidPercent(item.incomeRate)
          );
        });
        if (!hasCompleteRow) {
          this.$message.warning("收益共享至少需要填写一行有效数据");
          return;
        }

        const hasHalfFilledRow = this.formData.ruleList.some((item) => {
          const hasCapital = String(item.capitalRatio ?? "") !== "";
          const hasIncome = String(item.incomeRate ?? "") !== "";
          return hasCapital !== hasIncome;
        });
        if (hasHalfFilledRow) {
          this.$message.warning("请补全已填写的收益共享行");
          return;
        }

        const hasInvalidFilledRow = this.formData.ruleList.some((item) => {
          const hasCapital = String(item.capitalRatio ?? "") !== "";
          const hasIncome = String(item.incomeRate ?? "") !== "";
          if (!hasCapital && !hasIncome) {
            return false;
          }
          return (
            !this.isValidPercent(item.capitalRatio) ||
            !this.isValidPercent(item.incomeRate)
          );
        });
        if (hasInvalidFilledRow) {
          this.$message.warning("请填写1-100之间的整数");
          return;
        }
        this.$emit("submit", {
          templateName: this.formData.templateName,
          ruleList: this.formData.ruleList
            .filter((item) => {
              const hasCapital = String(item.capitalRatio ?? "") !== "";
              const hasIncome = String(item.incomeRate ?? "") !== "";
              return hasCapital && hasIncome;
            })
            .map((item) => {
              const row = {
                capitalRatio: Number(item.capitalRatio),
                incomeRate: Number(item.incomeRate),
              };
              if (item.id != null && item.id !== "") {
                row.id = item.id;
              }
              return row;
            }),
        });
      });
    },
    /** 弹窗关闭时重置表单并通知父组件 */
    handleClose() {
      this.$refs.formRef?.resetFields();
      this.$emit("close");
    },
  },
};
</script>

<style lang="scss" scoped>
.mode-title {
  background: #f5f7fa;
  border: 1px solid #ebeef5;
  border-bottom: none;
  height: 40px;
  line-height: 40px;
  padding: 0 12px;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.mode-content {
  border: 1px solid #ebeef5;
  padding: 16px 14px;
}

.edit-rule-row {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 10px;

  ::v-deep .el-input {
    width: 220px;
  }
}

.remove-icon {
  color: #909399;
  font-size: 18px;
  cursor: pointer;
}

.add-row {
  height: 36px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #303133;
  cursor: pointer;
  gap: 6px;
  margin-top: 6px;
}
</style>
